import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import {
  Chart as ChartJS,
  LinearScale,
  TimeScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
  Plugin,
  CategoryScale,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import { BehaviorType, Severity, GanttBarData, BEHAVIOR_LABELS, BEHAVIOR_COLORS } from './behaviorTypes';
import { TimeRangePreset, TIME_RANGE_PRESETS } from '../../utils/timezone';

// Register Chart.js components
ChartJS.register(
  LinearScale,
  TimeScale,
  CategoryScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

// ─── Row order for the V2 timeline ────────────────────────────────────────────
// Only show the 6 behaviors the user requested
const V2_ROW_ORDER: BehaviorType[] = ['sitting', 'standing', 'lying', 'walk', 'scratch', 'unknown'];

const V2_BEHAVIOR_LABELS: Record<BehaviorType, string> = {
  ...BEHAVIOR_LABELS,
};

// ─── Now Indicator Plugin ──────────────────────────────────────────────────────
const nowIndicatorPlugin: Plugin<'bar'> = {
  id: 'nowIndicatorV2',
  afterDraw(chart) {
    const xScale = chart.scales.x;
    const yScale = chart.scales.y;
    if (!xScale || !yScale) return;
    const now = Date.now();
    const xMin = xScale.min as number;
    const xMax = xScale.max as number;

    if (now < xMin || now > xMax) return;

    const xPixel = xScale.getPixelForValue(now);
    const ctx = chart.ctx;

    ctx.save();
    ctx.beginPath();
    ctx.setLineDash([6, 4]);
    ctx.strokeStyle = 'rgba(239, 68, 68, 0.7)';
    ctx.lineWidth = 1.5;
    ctx.moveTo(xPixel, yScale.top);
    ctx.lineTo(xPixel, yScale.bottom);
    ctx.stroke();
    ctx.setLineDash([]);

    // "now" label at top
    ctx.fillStyle = 'rgba(239, 68, 68, 0.85)';
    ctx.font = '10px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('now', xPixel, yScale.top - 4);
    ctx.restore();
  },
};

// ─── Alternating Row Background Plugin ──────────────────────────────────────────
const rowBackgroundPlugin: Plugin<'bar'> = {
  id: 'rowBackgroundV2',
  beforeDraw(chart) {
    const yScale = chart.scales.y;
    const xScale = chart.scales.x;
    if (!yScale || !xScale) return;
    const ctx = chart.ctx;

    for (let i = 0; i < V2_ROW_ORDER.length; i++) {
      if (i % 2 === 0) continue; // only shade odd rows
      const yPixel = yScale.getPixelForValue(i);
      const barHeight = yScale.height / V2_ROW_ORDER.length;

      ctx.save();
      ctx.fillStyle = 'rgba(156, 163, 175, 0.04)';
      ctx.fillRect(
        xScale.left,
        yPixel - barHeight / 2,
        xScale.width,
        barHeight
      );
      ctx.restore();
    }
  },
};

// ─── Time Ranges ───────────────────────────────────────────────────────────────
// Time range presets are now imported from ../../utils/timezone (TIME_RANGE_PRESETS)
// The component receives a TimeRangePreset prop from the parent instead of managing its own.

// Format a context range duration as a human-readable label (e.g., "24h", "3d", "7d", "30d")
function formatContextLabel(ms: number): string {
  const hours = ms / (60 * 60 * 1000);
  if (hours < 24) return `${Math.round(hours)}h`;
  const days = hours / 24;
  if (days < 14) return `${Math.round(days)}d`;
  return '30d';
}

// ─── Timeline Brush Selector ──────────────────────────────────────────────────
// A visual drag-to-select time range control built on top of a mini event density chart.
// The brush shows a configurable context range with a draggable window — the same data pool
// the main chart uses, giving users visual feedback of where events exist.

interface TimelineBrushProps {
  events: GanttBarData[];
  selectedStart: number;   // epoch ms — left edge of selection window
  selectedEnd: number;     // epoch ms — right edge of selection window
  contextEnd: number;      // epoch ms — fixed right edge of the brush track (does NOT move during drag)
  contextRange: number;    // total ms the brush track spans (e.g., 7 days)
  minStart: number;        // epoch ms — minimum allowed selectedStart (track left boundary or custom start)
  maxEnd: number;          // epoch ms — maximum allowed selectedEnd (track right boundary or custom end)
  maxSelectionMs?: number; // maximum selection width in ms (undefined = no limit)
  onBrushChange: (start: number, end: number) => void;
}

// Mini density chart data: count events per bucket over the context range
// Bucket granularity adapts to context range: ≤24H → 30min, ≤7D → 1hr, ≤30D → 4hr
// referenceTime: the timestamp representing "now" on the brush track (right edge)
function useEventDensity(pool: GanttBarData[], contextRange: number, referenceTime: number) {
  return useMemo(() => {
    const HOUR = 60 * 60 * 1000;
    const MIN = 60 * 1000;

    // Determine bucket size based on context range
    // Finer buckets for smaller ranges ensure enough bars for visual density
    let bucketMs: number;
    if (contextRange <= 1 * HOUR) {
      bucketMs = 5 * MIN;        // 5-min buckets for ≤1H context (12 buckets)
    } else if (contextRange <= 6 * HOUR) {
      bucketMs = 15 * MIN;       // 15-min buckets for ≤6H context (24 buckets)
    } else if (contextRange <= 24 * HOUR) {
      bucketMs = 30 * MIN;       // 30-min buckets for ≤24H context
    } else if (contextRange <= 7 * 24 * HOUR) {
      bucketMs = HOUR;           // 1-hour buckets for ≤7D context
    } else {
      bucketMs = 4 * HOUR;      // 4-hour buckets for ≤30D context
    }

    const buckets = Math.ceil(contextRange / bucketMs);
    const counts = new Array(buckets).fill(0);

    pool.forEach((e) => {
      const offset = referenceTime - e.endTime;
      if (offset < 0 || offset > contextRange) return;
      const bucketIdx = Math.floor(offset / bucketMs);
      if (bucketIdx >= 0 && bucketIdx < buckets) counts[buckets - 1 - bucketIdx]++;
    });

    const max = Math.max(...counts, 1);
    return counts.map((c) => c / max);
  }, [pool, contextRange, referenceTime]);
}

const TimelineBrush: React.FC<TimelineBrushProps> = ({
  events,
  selectedStart,
  selectedEnd,
  contextEnd,
  contextRange,
  minStart,
  maxEnd,
  maxSelectionMs,
  onBrushChange,
}) => {
  // contextEnd is the FIXED right edge of the brush track — it does NOT move during drag.
  // This is distinct from selectedEnd (the draggable right boundary of the selection window).
  // Using a fixed reference prevents the coordinate system from shifting during drag.
  const referenceTime = contextEnd;
  const brushRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef<'start' | 'end' | 'window' | 'select' | null>(null);
  const dragStartXRef = useRef<number>(0);
  const dragStartStartRef = useRef<number>(0);
  const dragStartEndRef = useRef<number>(0);
  const selectAnchorMsRef = useRef<number>(0); // anchor point for select-drag

  const density = useEventDensity(events, contextRange, referenceTime);

  // Convert a pixel X position inside the brush track to an epoch ms timestamp
  const pxToMs = useCallback(
    (clientX: number): number => {
      const rect = brushRef.current?.getBoundingClientRect();
      if (!rect) return referenceTime;
      const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      // left edge = referenceTime - contextRange, right edge = referenceTime
      return referenceTime - (1 - ratio) * contextRange;
    },
    [referenceTime, contextRange],
  );

  // ms range → percentage of brush width
  // Timeline: left edge (0%) = referenceTime - contextRange, right edge (100%) = referenceTime
  // A timestamp maps to: pct = (1 - (referenceTime - ts) / contextRange) * 100
  // selectedStart (earlier) → lower pct (closer to left/past)
  // selectedEnd (later) → higher pct (closer to right/referenceTime)
  const leftPct = (1 - (referenceTime - selectedStart) / contextRange) * 100;
  const rightPct = (1 - (referenceTime - selectedEnd) / contextRange) * 100;
  const windowPct = rightPct - leftPct;

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, handle: 'start' | 'end' | 'window') => {
      e.preventDefault();
      e.stopPropagation();
      draggingRef.current = handle;
      dragStartXRef.current = e.clientX;
      dragStartStartRef.current = selectedStart;
      dragStartEndRef.current = selectedEnd;
    },
    [selectedStart, selectedEnd],
  );

  // Click/drag on dimmed area to start a new selection
  const handleTrackMouseDown = useCallback(
    (e: React.MouseEvent) => {
      // Only respond to clicks on the track itself (not on handles/window)
      if ((e.target as HTMLElement).closest('[data-handle]')) return;
      e.preventDefault();
      const clickMs = pxToMs(e.clientX);
      draggingRef.current = 'select';
      dragStartXRef.current = e.clientX;
      dragStartStartRef.current = selectedStart;
      dragStartEndRef.current = selectedEnd;
      selectAnchorMsRef.current = clickMs;
      // Set initial selection to a tiny range at the click point
      onBrushChange(clickMs, clickMs + 60000);
    },
    [pxToMs, selectedStart, selectedEnd, onBrushChange],
  );

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!draggingRef.current) return;
      const rect = brushRef.current?.getBoundingClientRect();
      if (!rect) return;
      const dx = e.clientX - dragStartXRef.current;

      if (draggingRef.current === 'select') {
        // Drawing a new selection: anchor at click point, other edge follows mouse
        const anchorMs = selectAnchorMsRef.current;
        const currentMs = pxToMs(e.clientX);
        let newStart = Math.min(anchorMs, currentMs);
        let newEnd = Math.max(anchorMs, currentMs);
        // Enforce minimum 1 minute
        if (newEnd - newStart < 60000) return;
        // Enforce maximum selection width (if set)
        if (maxSelectionMs && newEnd - newStart > maxSelectionMs) {
          // Clamp from the anchor point: keep anchor, limit the other edge
          if (anchorMs === newStart) {
            newEnd = newStart + maxSelectionMs;
          } else {
            newStart = newEnd - maxSelectionMs;
          }
        }
        onBrushChange(
          Math.max(minStart, newStart),
          Math.min(maxEnd, newEnd),
        );
        return;
      }

      // For handle/window drags: positive dx = drag right on screen
      // On the timeline: right = referenceTime (higher timestamp), left = past (lower timestamp)
      // So dragging right (dx > 0) should increase timestamps.
      // dMs = pixel delta converted to ms delta. Positive when dragging right.
      const dMs = (dx / rect.width) * contextRange;

      if (draggingRef.current === 'start') {
        // Left handle controls selectedStart (the earlier boundary of the window)
        const newStart = dragStartStartRef.current + dMs;
        // Clamp: don't go before minStart, don't overlap end (min 1min gap),
        // and don't exceed max selection width (if set)
        let clampedStart = Math.max(minStart, Math.min(dragStartEndRef.current - 60000, newStart));
        if (maxSelectionMs) {
          clampedStart = Math.max(clampedStart, dragStartEndRef.current - maxSelectionMs);
        }
        onBrushChange(clampedStart, dragStartEndRef.current);
      } else if (draggingRef.current === 'end') {
        // Right handle controls selectedEnd (the later boundary of the window)
        const newEnd = dragStartEndRef.current + dMs;
        // Clamp: don't go past maxEnd, don't overlap start (min 1min gap),
        // and don't exceed max selection width (if set)
        let clampedEnd = Math.min(maxEnd, Math.max(dragStartStartRef.current + 60000, newEnd));
        if (maxSelectionMs) {
          clampedEnd = Math.min(clampedEnd, dragStartStartRef.current + maxSelectionMs);
        }
        onBrushChange(dragStartStartRef.current, clampedEnd);
      } else if (draggingRef.current === 'window') {
        // Entire window: shift both boundaries by the same delta
        const dur = dragStartEndRef.current - dragStartStartRef.current;
        let newStart = dragStartStartRef.current + dMs;
        // Clamp so window stays within [minStart, maxEnd - dur]
        newStart = Math.max(minStart, Math.min(newStart, maxEnd - dur));
        onBrushChange(newStart, newStart + dur);
      }
    };

    const handleMouseUp = (e: MouseEvent) => {
      if (draggingRef.current === 'select') {
        // If drag distance is small (< 5px), treat as a click → jump window to that point
        const dx = Math.abs(e.clientX - dragStartXRef.current);
        if (dx < 5) {
          const clickMs = pxToMs(e.clientX);
          let dur = dragStartEndRef.current - dragStartStartRef.current;
          // Enforce max selection width on click-to-jump (if set)
          if (maxSelectionMs && dur > maxSelectionMs) {
            dur = maxSelectionMs;
          }
          let newStart = clickMs - dur / 2;
          newStart = Math.max(minStart, Math.min(newStart, maxEnd - dur));
          onBrushChange(newStart, newStart + dur);
        }
      }
      draggingRef.current = null;
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [referenceTime, contextRange, minStart, maxEnd, maxSelectionMs, onBrushChange, pxToMs]);

  // Format a timestamp for the tooltip/label
  const fmtLabel = (ts: number) =>
    new Date(ts).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

  return (
    <div className="mb-4 p-3 bg-gray-50 dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-gray-700">
      {/* Labels row */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Drag to select · Click to jump</span>
        </div>
        <div className="flex items-center gap-3 text-xs">
          <span className="text-gray-400 dark:text-gray-500 font-mono">{fmtLabel(selectedStart)}</span>
          <span className="text-gray-300 dark:text-gray-600">→</span>
          <span className="text-gray-400 dark:text-gray-500 font-mono">{fmtLabel(selectedEnd)}</span>
          <span className="ml-1 px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 font-medium text-xs">
            {formatDuration(selectedEnd - selectedStart)}
          </span>
        </div>
      </div>

      {/* Brush track */}
      <div
        ref={brushRef}
        className="relative h-12 bg-gray-200 dark:bg-slate-700 rounded-lg overflow-hidden select-none cursor-crosshair"
        onMouseDown={handleTrackMouseDown}
      >
        {/* Event density bars */}
        <div className="absolute inset-0 flex items-end pointer-events-none">
          {density.map((h, i) => (
            <div
              key={i}
              className="flex-1 transition-none"
              style={{ height: `${Math.max(4, h * 100)}%` }}
            >
              <div
                className="w-full bg-blue-200 dark:bg-blue-800 opacity-60 transition-none"
                style={{ height: '100%' }}
              />
            </div>
          ))}
        </div>

        {/* Dimmed outer regions — clickable to start new selection */}
        <div
          className="absolute top-0 bottom-0 bg-gray-900/30 dark:bg-black/40 hover:bg-gray-900/40 dark:hover:bg-black/50 transition-colors"
          style={{ left: 0, width: `${leftPct}%` }}
        />
        <div
          className="absolute top-0 bottom-0 bg-gray-900/30 dark:bg-black/40 hover:bg-gray-900/40 dark:hover:bg-black/50 transition-colors"
          style={{ right: 0, width: `${100 - rightPct}%` }}
        />

        {/* Selected window highlight — blue tint */}
        <div
          className="absolute top-0 bottom-0 bg-blue-500/10 dark:bg-blue-400/10 pointer-events-none"
          style={{ left: `${leftPct}%`, width: `${windowPct}%` }}
        />

        {/* Draggable window body — rendered BEFORE handles so handles are on top */}
        <div
          data-handle="window"
          className="absolute top-0 bottom-0 cursor-grab active:cursor-grabbing"
          style={{ left: `${leftPct}%`, width: `${windowPct}%` }}
          onMouseDown={(e) => handleMouseDown(e, 'window')}
        >
          <div className="absolute inset-0 border-t-2 border-b-2 border-blue-300/60 dark:border-blue-600/60 transition-opacity" />
          {/* Drag hint grip — always visible when window is wide enough */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col gap-0.5 opacity-40 hover:opacity-80 transition-opacity">
            {[0, 1, 2].map((i) => (
              <div key={i} className="w-4 h-0.5 rounded-full bg-blue-400 dark:bg-blue-300" />
            ))}
          </div>
        </div>

        {/* Selected window border overlay */}
        <div
          className="absolute top-0 bottom-0 border-l-2 border-r-2 border-blue-400 dark:border-blue-500 pointer-events-none z-10"
          style={{ left: `${leftPct}%`, width: `${windowPct}%` }}
        />

        {/* Start handle (left edge, controls selectedStart) */}
        <div
          data-handle="start"
          className="absolute top-0 bottom-0 w-4 cursor-ew-resize flex items-center justify-center group z-20"
          style={{ left: `${leftPct}%`, transform: 'translateX(-50%)' }}
          onMouseDown={(e) => handleMouseDown(e, 'start')}
        >
          <div className="w-1 h-8 rounded-full bg-blue-500 dark:bg-blue-400 shadow-md group-hover:w-1.5 group-hover:shadow-lg transition-all" />
        </div>

        {/* End handle (right edge, controls selectedEnd) */}
        <div
          data-handle="end"
          className="absolute top-0 bottom-0 w-4 cursor-ew-resize flex items-center justify-center group z-20"
          style={{ left: `${rightPct}%`, transform: 'translateX(-50%)' }}
          onMouseDown={(e) => handleMouseDown(e, 'end')}
        >
          <div className="w-1 h-8 rounded-full bg-blue-500 dark:bg-blue-400 shadow-md group-hover:w-1.5 group-hover:shadow-lg transition-all" />
        </div>
      </div>

      {/* Time axis labels */}
      <div className="flex items-center justify-between mt-1 px-1">
        <span className="text-[10px] text-gray-400 dark:text-gray-500 font-mono">{formatContextLabel(contextRange)} ago</span>
        <span className="text-[10px] text-gray-400 dark:text-gray-500 font-mono">
          {Math.abs(referenceTime - Date.now()) < 60000 ? 'Now' : formatTime(referenceTime)}
        </span>
      </div>
    </div>
  );
};

// ─── Format helpers ─────────────────────────────────────────────────────────────

function formatDuration(ms: number): string {
  const totalSeconds = Math.round(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes < 60) return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours < 24) return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  const days = Math.floor(hours / 24);
  const remainHours = hours % 24;
  return remainHours > 0 ? `${days}d ${remainHours}h` : `${days}d`;
}

function formatTime(ts: number): string {
  return new Date(ts).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

// ─── Sample Event Pool ──────────────────────────────────────────────────────────
//
// Generates a fixed pool of behavior events with absolute timestamps spanning
// the last 7 days. Used as a demo/preview when no real data is available.
// Toggle the "Sample Data" button to switch between real and sample data.

function generateSampleEventPool(): GanttBarData[] {
  const now = Date.now();
  const HOUR = 60 * 60 * 1000;
  const MIN = 60 * 1000;
  const events: GanttBarData[] = [];
  let id = 1;

  // Helper: add an event with absolute timestamps
  const ev = (type: BehaviorType, startHrsAgo: number, durMins: number, severity: Severity, confidence: number) => {
    const start = now - startHrsAgo * HOUR;
    const end = start + durMins * MIN;
    events.push({ id: `s${id++}`, type, startTime: start, endTime: end, severity, confidence });
  };

  // ── Sitting events (various times over last 7 days) ──────────────────────
  ev('sitting', 0.5, 25, 'normal', 0.92);
  ev('sitting', 2, 45, 'normal', 0.88);
  ev('sitting', 5, 30, 'elevated', 0.85);
  ev('sitting', 12, 60, 'normal', 0.90);
  ev('sitting', 24, 40, 'normal', 0.87);
  ev('sitting', 48, 55, 'elevated', 0.83);
  ev('sitting', 96, 35, 'normal', 0.91);
  ev('sitting', 144, 50, 'normal', 0.86);

  // ── Standing events ──────────────────────────────────────────────────────
  ev('standing', 1, 15, 'normal', 0.88);
  ev('standing', 4, 20, 'normal', 0.84);
  ev('standing', 8, 10, 'elevated', 0.80);
  ev('standing', 18, 25, 'normal', 0.86);
  ev('standing', 36, 15, 'normal', 0.82);
  ev('standing', 72, 20, 'normal', 0.89);
  ev('standing', 120, 12, 'elevated', 0.79);

  // ── Lying events ─────────────────────────────────────────────────────────
  ev('lying', 0.3, 40, 'normal', 0.95);
  ev('lying', 3, 90, 'normal', 0.91);
  ev('lying', 7, 60, 'alert', 0.93);
  ev('lying', 15, 120, 'normal', 0.89);
  ev('lying', 30, 45, 'normal', 0.87);
  ev('lying', 60, 80, 'elevated', 0.86);
  ev('lying', 84, 100, 'normal', 0.92);
  ev('lying', 132, 70, 'normal', 0.88);
  ev('lying', 156, 55, 'alert', 0.90);

  // ── Walking events ───────────────────────────────────────────────────────
  ev('walk', 0.8, 10, 'normal', 0.87);
  ev('walk', 3.5, 15, 'normal', 0.83);
  ev('walk', 10, 12, 'normal', 0.85);
  ev('walk', 22, 8, 'elevated', 0.79);
  ev('walk', 42, 20, 'normal', 0.81);
  ev('walk', 78, 15, 'normal', 0.84);
  ev('walk', 108, 10, 'normal', 0.88);
  ev('walk', 150, 18, 'elevated', 0.77);

  // ── Scratching events ────────────────────────────────────────────────────
  ev('scratch', 1.5, 5, 'elevated', 0.78);
  ev('scratch', 6, 8, 'alert', 0.91);
  ev('scratch', 14, 4, 'elevated', 0.82);
  ev('scratch', 28, 6, 'normal', 0.76);
  ev('scratch', 54, 5, 'alert', 0.89);
  ev('scratch', 90, 7, 'elevated', 0.80);
  ev('scratch', 138, 4, 'normal', 0.74);

  // ── Unknown events ───────────────────────────────────────────────────────
  ev('unknown', 4, 8, 'normal', 0.55);
  ev('unknown', 20, 12, 'normal', 0.50);
  ev('unknown', 66, 10, 'normal', 0.48);
  ev('unknown', 114, 6, 'normal', 0.52);

  return events;
}

// ─── Custom Date/Time Picker ──────────────────────────────────────────────────
// Two datetime-local inputs for precise start/end date selection.
// Bidirectionally synced with the TimelineBrush — changing one updates the other.

interface CustomDatePickerProps {
  brushStart: number;
  brushEnd: number;
  onBrushChange: (start: number, end: number) => void;
  onRangeChange: (start: number, end: number) => void;
}

const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  brushStart,
  brushEnd,
  onBrushChange,
  onRangeChange,
}) => {
  // Convert epoch ms to datetime-local string (YYYY-MM-DDTHH:mm)
  const msToDatetimeLocal = (ms: number): string => {
    const d = new Date(ms);
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  // Convert datetime-local string to epoch ms
  const datetimeLocalToMs = (s: string): number => {
    const d = new Date(s);
    return isNaN(d.getTime()) ? 0 : d.getTime();
  };

  const [startStr, setStartStr] = useState(() => msToDatetimeLocal(brushStart));
  const [endStr, setEndStr] = useState(() => msToDatetimeLocal(brushEnd));
  const [error, setError] = useState<string | null>(null);

  // Sync from brush state to local inputs (when brush is dragged)
  useEffect(() => {
    setStartStr(msToDatetimeLocal(brushStart));
    setEndStr(msToDatetimeLocal(brushEnd));
  }, [brushStart, brushEnd]);

  const handleApply = useCallback(() => {
    const startMs = datetimeLocalToMs(startStr);
    const endMs = datetimeLocalToMs(endStr);

    if (startMs === 0 || endMs === 0) {
      setError('Invalid date format');
      return;
    }
    if (startMs >= endMs) {
      setError('Start must be before end');
      return;
    }
    if (endMs - startMs < 60000) {
      setError('Minimum range is 1 minute');
      return;
    }

    // Pass the user's raw selection through unchanged. No silent clamping —
    // see TimeRangeSelector.tsx for the rationale.
    setError(null);
    onBrushChange(startMs, endMs);
    onRangeChange(startMs, endMs);
  }, [startStr, endStr, onBrushChange, onRangeChange]);

  const handleReset = useCallback(() => {
    const now = Date.now();
    const defaultStart = now - 6 * 60 * 60 * 1000;
    setStartStr(msToDatetimeLocal(defaultStart));
    setEndStr(msToDatetimeLocal(now));
    setError(null);
    onBrushChange(defaultStart, now);
    onRangeChange(defaultStart, now);
  }, [onBrushChange, onRangeChange]);

  return (
    <div className="mb-4 p-3 bg-gray-50 dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-gray-700">
      <div className="flex flex-wrap items-end gap-3">
        {/* Start date/time */}
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
            Start Date & Time
          </label>
          <input
            type="datetime-local"
            value={startStr}
            onChange={(e) => { setStartStr(e.target.value); setError(null); }}
            className="w-full px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
          />
        </div>

        {/* End date/time */}
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
            End Date & Time
          </label>
          <input
            type="datetime-local"
            value={endStr}
            onChange={(e) => { setEndStr(e.target.value); setError(null); }}
            className="w-full px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
          />
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleApply}
            className="px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition-colors"
          >
            Apply
          </button>
          <button
            onClick={handleReset}
            className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-600 transition-colors"
          >
            Reset
          </button>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <p className="mt-2 text-xs text-red-500 dark:text-red-400">{error}</p>
      )}

      {/* Duration badge */}
      <div className="mt-2 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
        <span>Duration:</span>
        <span className="px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 font-medium">
          {formatDuration(brushEnd - brushStart)}
        </span>
      </div>
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────

interface BehaviorTimelineV2Props {
  events?: GanttBarData[];
  /** Time range preset from the parent (e.g. '6h', '24h'). Controls the brush context range. */
  timeRange?: TimeRangePreset;
  /** Custom time range (from/to epoch ms). When provided, overrides the preset and sets brush to this range. */
  customTimeRange?: { from: number; to: number } | null;
  onBarClick?: (bar: GanttBarData) => void;
  /** When true, show every individual detection (no merging). When false, merge nearby same-type bars. */
  detailMode?: boolean;
  /** Callback when detail mode changes (for parent to re-compute bars). */
  onDetailModeChange?: (detail: boolean) => void;
  /** Callback when the brush selection changes (start/end in epoch ms). */
  onBrushChange?: (start: number, end: number) => void;
}

/** Convert a TimeRangePreset key to minutes. */
function presetToMinutes(preset: TimeRangePreset): number {
  return TIME_RANGE_PRESETS.find(p => p.key === preset)?.minutes ?? 360;
}

const BehaviorTimelineV2: React.FC<BehaviorTimelineV2Props> = ({
  events,
  timeRange: timeRangeProp = '6h',
  customTimeRange = null,
  onBarClick,
  detailMode: detailModeProp,
  onDetailModeChange,
  onBrushChange,
}) => {
  // Detail mode state: true = show every detection, false = merge nearby same-type bars
  const [detailModeInternal, setDetailModeInternal] = useState(true);
  const detailMode = detailModeProp ?? detailModeInternal;
  // Internal time range in minutes, derived from the prop
  const [timeRangeMinutes, setTimeRangeMinutes] = useState(() => presetToMinutes(timeRangeProp));
  // Sync internal minutes when the prop changes
  useEffect(() => {
    const minutes = presetToMinutes(timeRangeProp);
    setTimeRangeMinutes(minutes);
    // Also reset brush to the new preset range, data-aware
    if (!useSampleData && sourceEvents.length > 0) {
      const dataEnd = Math.max(...sourceEvents.map((e) => e.endTime));
      const now = Date.now();
      const anchor = dataEnd < now - 60000 ? dataEnd : now;
      setBrushStart(anchor - minutes * 60000);
      setBrushEnd(anchor);
      setContextEnd(anchor);
    } else {
      const now = Date.now();
      setBrushStart(now - minutes * 60000);
      setBrushEnd(now);
      setContextEnd(now);
    }
    setCustomMode(false);
  }, [timeRangeProp]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync brush to custom time range from parent (overrides preset)
  useEffect(() => {
    if (!customTimeRange) return;
    const { from, to } = customTimeRange;
    const minutes = Math.max(1, Math.round((to - from) / 60000));
    setTimeRangeMinutes(minutes);
    setBrushStart(from);
    setBrushEnd(to);
    setContextEnd(to);
    setCustomContextRange(to - from);
    setCustomMode(true);
  }, [customTimeRange]); // eslint-disable-line react-hooks/exhaustive-deps

  // Emit initial brush state to parent on mount so the export "Use brush range" button
  // becomes active immediately (instead of requiring user interaction first).
  useEffect(() => {
    if (onBrushChange) {
      onBrushChange(brushStart, brushEnd);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount

  const [customMode, setCustomMode] = useState(false);
  const [useSampleData, setUseSampleData] = useState(false);
  const [brushStart, setBrushStart] = useState(() => Date.now() - 6 * 60 * 60 * 1000);
  const [brushEnd, setBrushEnd] = useState(() => Date.now());
  // contextEnd: the FIXED right edge of the brush track. Does NOT move during drag.
  // In preset mode: set to the anchor time (data end or now) when a preset is clicked.
  // In custom mode: set to the custom range end when Apply is clicked.
  const [contextEnd, setContextEnd] = useState(() => Date.now());
  // customContextRange: the FIXED context range for custom mode (set on Apply, not recomputed from brush).
  // Mirrors how presets use timeRange * 60000 as a fixed value. Prevents the track from
  // rescaling when the user drags the brush edges in custom mode.
  const [customContextRange, setCustomContextRange] = useState(() => 6 * 60 * 60 * 1000);
  // Track if Zoom to Data was just clicked for visual feedback
  const [zoomToDataJustClicked, setZoomToDataJustClicked] = useState(false);
  const chartRef = useRef<any>(null);

  // Compute context range for the brush based on current selection
  const contextRange = useMemo(() => {
    if (customMode) {
      // Custom mode: use the fixed customContextRange (set on Apply, not recomputed from brush).
      // This prevents the track from rescaling when the user drags the brush edges.
      return customContextRange;
    }
    // Preset mode: context range = preset duration (brush track shows exactly the preset)
    // This locks the brush to the preset boundary — user can narrow but not exceed it
    return timeRangeMinutes * 60000;
  }, [customMode, timeRangeMinutes, customContextRange]);

  // Effective time range in minutes — reflects the actual visible duration for chart axis config.
  // In custom mode, this is derived from the brush width; in preset mode, it's the preset duration.
  const effectiveTimeRange = useMemo(() => {
    if (customMode) {
      return Math.round((brushEnd - brushStart) / 60000);
    }
    return timeRangeMinutes;
  }, [customMode, brushStart, brushEnd, timeRangeMinutes]);

  // Compute the boundary constraints for the brush based on mode
  const minStart = useMemo(() => {
    // Both modes: allow dragging within the context range (which includes padding)
    // This lets users pan the brush within the visible track area
    return contextEnd - contextRange;
  }, [contextEnd, contextRange]);

  const maxEnd = useMemo(() => {
    // Both modes: allow dragging up to the context end
    return contextEnd;
  }, [contextEnd]);

  // Time window — always use brush range (brush is always visible)
  const timeWindow = useMemo(() => {
    return { timeStart: brushStart, timeEnd: brushEnd };
  }, [brushStart, brushEnd]);

  // Sample event pool — generated once per mount, spans last 7 days
  const samplePool = useMemo(() => generateSampleEventPool(), []);

  // Use sample data when toggled on, otherwise use real events from props
  const sourceEvents = useSampleData ? samplePool : (events ?? []);

  // Filter to only V2 behaviors and visible time range
  const filteredEvents = useMemo(
    () => sourceEvents.filter(
      (e) => V2_ROW_ORDER.includes(e.type) && e.endTime >= timeWindow.timeStart && e.startTime <= timeWindow.timeEnd
    ),
    [sourceEvents, timeWindow.timeStart, timeWindow.timeEnd]
  );

  const hasData = filteredEvents.length > 0;

  // ── Auto-fit brush to data range when data is outside the current window ──
  // When real data arrives but none is visible (all outside brush range),
  // automatically adjust the brush to encompass the data.
  const zoomToDataRange = useCallback(() => {
    if (sourceEvents.length === 0) return;
    const dataStart = Math.min(...sourceEvents.map((e) => e.startTime));
    const dataEnd = Math.max(...sourceEvents.map((e) => e.endTime));
    const dataRange = dataEnd - dataStart;
    const padding = Math.max(dataRange * 0.05, 60000); // 5% padding, min 1 minute
    const newStart = dataStart - padding;
    const newEnd = dataEnd + padding;
    setBrushStart(newStart);
    setBrushEnd(newEnd);
    setContextEnd(newEnd);
    setCustomContextRange(newEnd - newStart);
    setCustomMode(true);
    // Notify parent of brush change
    if (onBrushChange) {
      onBrushChange(newStart, newEnd);
    }
    // Visual feedback: flash the button blue briefly
    setZoomToDataJustClicked(true);
    setTimeout(() => setZoomToDataJustClicked(false), 600);
  }, [sourceEvents, onBrushChange]);

  // NOTE: Auto-fit is intentionally NOT automatic. It was causing a bug where
  // click-to-jump in the brush would trigger auto-fit (switching to custom mode)
  // when the brush landed on an area with no events. Instead, users can explicitly
  // trigger auto-fit via the "Zoom to data range" button in the empty state.

  // Build Chart.js floating bar datasets
  const chartData = useMemo(() => {
    const datasets = V2_ROW_ORDER.map((type, rowIndex) => {
      const typeEvents = filteredEvents
        .filter((e) => e.type === type)
        .sort((a, b) => a.startTime - b.startTime);

      return {
        label: V2_BEHAVIOR_LABELS[type],
        data: typeEvents.map((e) => ({
          x: [e.startTime, e.endTime] as [number, number], // floating bar [start, end]
          y: rowIndex,
          _event: e, // store event for tooltip
        })),
        backgroundColor: typeEvents.map((e) => {
          const colors = BEHAVIOR_COLORS[type];
          if (e.severity === 'alert') return colors.alert + 'CC';
          if (e.severity === 'elevated') return colors.elevated + 'CC';
          return colors.normal + 'CC';
        }),
        borderColor: typeEvents.map((e) => {
          const colors = BEHAVIOR_COLORS[type];
          if (e.severity === 'alert') return colors.alert;
          if (e.severity === 'elevated') return colors.elevated;
          return colors.normal;
        }),
        borderWidth: 1.5,
        borderRadius: 4,
        borderSkipped: false as const,
        barPercentage: 0.75,
        categoryPercentage: 1.0,
        grouped: false,
        hoverBackgroundColor: typeEvents.map((e) => {
          const colors = BEHAVIOR_COLORS[type];
          if (e.severity === 'alert') return colors.alert;
          if (e.severity === 'elevated') return colors.elevated;
          return colors.normal;
        }),
        hoverBorderColor: typeEvents.map((e) => {
          const colors = BEHAVIOR_COLORS[type];
          if (e.severity === 'alert') return colors.alert;
          if (e.severity === 'elevated') return colors.elevated;
          return colors.normal;
        }),
        hoverBorderWidth: 2.5,
      };
    });

    return { datasets };
  }, [filteredEvents]);

  // Chart options
  const options = useMemo((): ChartOptions<'bar'> => ({
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'nearest' as const,
      intersect: true,
    },
    scales: {
      x: {
        type: 'time' as const,
        time: {
          unit: effectiveTimeRange <= 60 ? 'minute' : effectiveTimeRange <= 720 ? 'hour' : effectiveTimeRange <= 4320 ? 'hour' : 'day',
          displayFormats: {
            minute: 'HH:mm',
            hour: 'MMM d HH:mm',
            day: 'MMM d',
          },
          tooltipFormat: 'MMM d, yyyy HH:mm',
        },
        min: timeWindow.timeStart,
        max: timeWindow.timeEnd,
        grid: {
          color: 'rgba(156, 163, 175, 0.1)',
        },
        ticks: {
          color: 'rgba(156, 163, 175, 0.8)',
          font: { size: 10 },
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: effectiveTimeRange <= 60 ? 12 : effectiveTimeRange <= 720 ? 8 : 10,
        },
        title: {
          display: false,
        },
      },
      y: {
        type: 'linear' as const,
        min: -0.5,
        max: V2_ROW_ORDER.length - 0.5,
        grid: {
          color: (ctx) => {
            const val = ctx.tick?.value;
            if (val != null && Number.isInteger(val) && val >= 0 && val < V2_ROW_ORDER.length) {
              return 'rgba(156, 163, 175, 0.15)';
            }
            return 'transparent';
          },
        },
        ticks: {
          color: 'rgba(156, 163, 175, 0.9)',
          font: { size: 11, weight: 'bold' as const },
          padding: 8,
          stepSize: 1,
          maxTicksLimit: V2_ROW_ORDER.length,
          autoSkip: false,
          callback: (value: number | string) => {
            const numVal = typeof value === 'string' ? parseFloat(value) : value;
            if (numVal != null && Number.isInteger(numVal) && numVal >= 0 && numVal < V2_ROW_ORDER.length) {
              return V2_BEHAVIOR_LABELS[V2_ROW_ORDER[numVal]];
            }
            return '';
          },
        },
        title: {
          display: false,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(15, 23, 42, 0.92)',
        borderColor: 'rgba(148, 163, 184, 0.3)',
        borderWidth: 1,
        cornerRadius: 8,
        padding: 12,
        titleFont: { size: 13, weight: 'bold' as const },
        bodyFont: { size: 12 },
        titleColor: '#e2e8f0',
        bodyColor: '#cbd5e1',
        displayColors: true,
        boxPadding: 4,
        callbacks: {
          title: (items) => {
            if (!items.length) return '';
            const raw = items[0].raw as any;
            const event = raw?._event as GanttBarData | undefined;
            if (!event) return '';
            return V2_BEHAVIOR_LABELS[event.type];
          },
          label: (context) => {
            const raw = context.raw as any;
            const event = raw?._event as GanttBarData | undefined;
            if (!event) return '';
            const duration = event.endTime - event.startTime;
            const lines: string[] = [];
            lines.push(`Time: ${formatTime(event.startTime)} → ${formatTime(event.endTime)}`);
            lines.push(`Duration: ${formatDuration(duration)}`);
            if (event.poseCount && event.poseCount > 1) {
              lines.push(`Detections: ${event.poseCount}`);
            }
            lines.push(`Severity: ${event.severity.charAt(0).toUpperCase() + event.severity.slice(1)}`);
            if (event.confidence) {
              lines.push(`Confidence: ${Math.round(event.confidence * 100)}%`);
            }
            return lines;
          },
        },
      },
    },
    onClick: (_event, elements) => {
      if (elements.length > 0 && onBarClick) {
        const datasetIndex = elements[0].datasetIndex;
        const index = elements[0].index;
        const dataPoint = chartData.datasets[datasetIndex]?.data[index];
        const event = dataPoint?._event as GanttBarData | undefined;
        if (event) onBarClick(event);
      }
    },
  }), [effectiveTimeRange, timeWindow.timeStart, timeWindow.timeEnd, onBarClick]);

  const handleBrushChange = useCallback((start: number, end: number) => {
    setBrushStart(start);
    setBrushEnd(end);
    // Notify parent of brush change
    if (onBrushChange) {
      onBrushChange(start, end);
    }
  }, [onBrushChange]);

  return (
    <div className="rounded-xl dark:bg-claude-darkSurfaceMuted bg-claude-surfaceMuted p-6 border border-claude-border dark:border-claude-darkBorder">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-claude-darkText">
            Behavior Timeline V2
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {useSampleData
              ? `📋 Sample data — ${customMode ? `Custom range: ${formatDuration(brushEnd - brushStart)}` : `Last ${timeRangeMinutes / 60} hour${timeRangeMinutes / 60 !== 1 ? 's' : ''}`}`
              : customMode
                ? `Custom range: ${formatDuration(brushEnd - brushStart)} selected`
                : `Last ${timeRangeMinutes / 60} hour${timeRangeMinutes / 60 !== 1 ? 's' : ''} — Floating bar view`
            }
          </p>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-2">
          <button
            onClick={zoomToDataRange}
            className={`px-3 py-1 text-xs font-medium rounded-md transition-all border ${
              zoomToDataJustClicked
                ? 'bg-blue-500 text-white border-blue-500'
                : 'bg-transparent text-gray-500 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 hover:text-gray-700 dark:hover:text-white'
            }`}
            title="Zoom to fit all detected behavior data"
          >
            Zoom to Data
          </button>
          <button
            onClick={() => {
              const next = !detailMode;
              if (onDetailModeChange) {
                onDetailModeChange(next);
              } else {
                setDetailModeInternal(next);
              }
            }}
            className={`px-3 py-1 text-xs font-medium rounded-md transition-all border ${
              detailMode
                ? 'bg-emerald-500 text-white border-emerald-500'
                : 'bg-yellow-400 text-yellow-900 border-yellow-400 hover:bg-yellow-500 hover:border-yellow-500'
            }`}
            title={detailMode ? 'Detail mode: showing every detection without merging' : 'General mode: merging nearby same-type detections'}
          >
            {detailMode ? '🔍 Detail' : '📊 General'}
          </button>
          <button
            onClick={() => setUseSampleData(prev => !prev)}
            className={`px-3 py-1 text-xs font-medium rounded-md transition-all border ${
              useSampleData
                ? 'bg-amber-500 text-white border-amber-500'
                : 'bg-transparent text-gray-500 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
            }`}
            title={useSampleData ? 'Showing sample data — click to switch to real data' : 'Showing real data — click to load sample data'}
          >
            {useSampleData ? 'Sample Data' : 'Real Data'}
          </button>
        </div>
      </div>

      {/* Timeline Brush — always visible with adaptive context range */}
      <TimelineBrush
        events={sourceEvents}
        selectedStart={brushStart}
        selectedEnd={brushEnd}
        contextEnd={contextEnd}
        contextRange={contextRange}
        minStart={minStart}
        maxEnd={maxEnd}
        maxSelectionMs={customMode ? customContextRange : timeRangeMinutes * 60000}
        onBrushChange={handleBrushChange}
      />

      {/* Custom Date/Time Picker — visible only in custom mode */}
      {customMode && (
        <CustomDatePicker
          brushStart={brushStart}
          brushEnd={brushEnd}
          onBrushChange={handleBrushChange}
          onRangeChange={(start, end) => {
            setContextEnd(end);
            setCustomContextRange(end - start);
            if (onBrushChange) {
              onBrushChange(start, end);
            }
          }}
        />
      )}

      {/* Chart */}
      {hasData ? (
        <>
          <div style={{ height: '320px', position: 'relative' }}>
            <Bar
              key={`v2-${timeRangeMinutes}`}
              ref={chartRef}
              data={chartData as any}
              options={options as any}
              plugins={[nowIndicatorPlugin, rowBackgroundPlugin]}
            />
          </div>

          {/* Legend */}
          <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700 flex flex-wrap gap-x-4 gap-y-2 text-xs">
            {V2_ROW_ORDER.map((type) => (
              <div key={type} className="flex items-center gap-1.5">
                <div
                  className="w-6 h-2.5 rounded-sm"
                  style={{ backgroundColor: BEHAVIOR_COLORS[type].normal }}
                />
                <span className="text-gray-600 dark:text-gray-400">{V2_BEHAVIOR_LABELS[type]}</span>
              </div>
            ))}
            <div className="flex items-center gap-3 ml-auto">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-sm bg-yellow-400 opacity-80" />
                <span className="text-gray-500 dark:text-gray-400">Elevated</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-sm bg-red-500 opacity-80" />
                <span className="text-gray-500 dark:text-gray-400">Alert</span>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="h-48 flex flex-col items-center justify-center text-gray-400 dark:text-gray-500">
          <svg
            className="w-12 h-12 mb-3 opacity-40"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z"
            />
          </svg>
          {sourceEvents.length > 0 ? (
            <>
              <p className="text-sm">
                {sourceEvents.length} behavior event{sourceEvents.length !== 1 ? 's' : ''} detected, but none in the selected time range
              </p>
              <button
                onClick={zoomToDataRange}
                className="mt-3 px-4 py-1.5 text-xs font-medium rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition-colors"
              >
                Zoom to data range
              </button>
            </>
          ) : (
            <>
              <p className="text-sm">
                No behaviors detected in the selected time range
              </p>
              <p className="text-xs mt-1 opacity-60">Start a monitoring session to see the timeline</p>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default BehaviorTimelineV2;
export type { BehaviorTimelineV2Props };